/**
 * Display functions for view field rendering.
 * String functions return a formatted string.
 * Component functions return a Vue functional component object for template rendering.
 */

import CompanyInfoPopup from "@/components/Offers/CompanyInfoPopup.vue";

export function displayPercentValue(value, decimals = 0) {
    if (value == null) {
        return "—";
    }
    const factor = Math.pow(10, decimals);
    const rounded = Math.round(Number(value) * factor) / factor;
    return `${rounded}%`;
}

export function displayCurrencyValue(value, decimals = 0) {
    if (value == null) {
        return "—";
    }
    const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    return formatter.format(Number(value));
}

export function displayPositiveCurrencyValue(value, decimals = 0) {
    if (value == null) {
        return "—";
    }
    const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    return formatter.format(Math.abs(Number(value)));
}

export function displayNumericValue(value, decimals = 0) {
    if (value == null) {
        return "—";
    }
    return Number(value).toFixed(decimals);
}

export function displayDateValue(value) {
    if (value == null) {
        return "—";
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return value;
    }
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
}

export function displayBoolValue(value) {
    if (value == null) {
        return "—";
    }
    return value ? "Yes" : "No";
}

export function displayContingencyValue(value) {
    if (value == null || value === "") {
        return "No";
    }
    return value;
}

/**
 * Returns a functional Vue component that renders <company-info-popup>.
 * fieldName should point to a company object (e.g. escrowCompany, buyerCompany).
 */
export function companyPopover(value) {
    return {
        functional: true,
        render: (h) => h(CompanyInfoPopup, { props: { companyInfo: value } }),
        _cssClass: "offers-table-escrow-info",
    };
}

/**
 * Returns a functional Vue component that renders broker fee (percent + amount).
 * Receives the full source object (fieldName should be null in screenField).
 */
export function brokerFee(value) {
    return {
        functional: true,
        render(h) {
            const source = value || {};
            const { brokerFeePercent, brokerFeeAmount } = source;
            if (!brokerFeePercent && !brokerFeeAmount) {
                return h("span", "—");
            }
            const parts = [];
            if (brokerFeePercent) {
                parts.push(h("span", `${brokerFeePercent}%`));
            }
            if (brokerFeePercent && brokerFeeAmount) {
                parts.push(h("span", " ("));
            }
            if (brokerFeeAmount) {
                const fmt = new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                });
                parts.push(h("span", fmt.format(brokerFeeAmount)));
            }
            if (brokerFeePercent && brokerFeeAmount) {
                parts.push(h("span", ")"));
            }
            return h("span", parts);
        },
    };
}

const DISPLAY_FUNCTIONS = {
    displayPercentValue,
    displayCurrencyValue,
    displayPositiveCurrencyValue,
    displayNumericValue,
    displayDateValue,
    displayBoolValue,
    displayContingencyValue,
    companyPopover,
    brokerFee,
};

/**
 * Parses a displayFunction string like "displayPercentValue(2)" or "displayPercentValue"
 * and applies it to the given value.
 * Returns the raw value unchanged if the function is not found or the string is empty.
 * @param {string|null} funcString
 * @param {*} value
 * @returns {string|object}
 */
export function applyDisplayFunction(funcString, value) {
    if (!funcString || !funcString.trim()) {
        return value;
    }
    const match = funcString.trim().match(/^(\w+)(?:\((\d+)\))?$/);
    if (!match) return value;
    const funcName = match[1];
    const decimals = match[2] !== undefined ? Number(match[2]) : 0;
    const fn = DISPLAY_FUNCTIONS[funcName];
    if (!fn) {
        return value;
    }
    return fn(value, decimals);
}
